import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, BookOpen, RefreshCw, ChevronDown, ChevronUp, Home, Settings } from 'lucide-react';
import CircularProgress from './CircularProgress';
import './CollapsibleHeader.css';

const CollapsibleHeader = ({ 
  subject, 
  onBack, 
  onViewStudyPlan, 
  onUpdateGuide,
  onSettings,
  isStudying, 
  useTestData,
  progress = { completed: 0, total: 100 },
  guideData = null,
  quotaStatus = null
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!isStudying);

  // Auto-collapse when studying starts
  useEffect(() => {
    setIsExpanded(!isStudying);
  }, [isStudying]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (isStudying && !isHovered && !isExpanded) {
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          // Scrolling down and past threshold - hide header
          setIsVisible(false);
        } else if (currentScrollY < lastScrollY) {
          // Scrolling up - show header
          setIsVisible(true);
        }
      } else {
        // Not studying or expanded - always show header
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isStudying, lastScrollY, isHovered, isExpanded]);

  const progressPercentage = Math.round((progress.completed / progress.total) * 100);

  return (
    <AnimatePresence>
      <motion.header 
        className={`collapsible-header ${isStudying ? 'studying' : ''} ${isExpanded ? 'expanded' : 'collapsed'}`}
        initial={{ y: 0 }}
        animate={{ y: isVisible ? 0 : -100 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="container">
          {/* Always visible header bar */}
          <div className="header-bar">
            {/* Essential navigation items always shown */}
            <div className="header-essential">
              <button className="back-button-minimal" onClick={onBack}>
                <span className="back-icon-desktop">
                  <ArrowLeft size={18} />
                </span>
                <span className="back-icon-mobile">
                  <Home size={18} />
                </span>
                <span className="back-text">Back to Dashboard</span>
              </button>
              
              <div className="subject-icon-small">
                <BookOpen size={20} />
              </div>
              
              <h1 className="subject-title">{subject?.name}</h1>
            </div>

            {/* Progress or loading spinner always visible but smaller when collapsed */}
            <div className="header-progress">
              {guideData ? (
                <CircularProgress 
                  percentage={progressPercentage}
                  size={isExpanded ? 60 : 45}
                  strokeWidth={isExpanded ? 4 : 6}
                />
              ) : (
                <div className="loading-spinner" style={{ width: isExpanded ? 60 : 45, height: isExpanded ? 60 : 45 }}>
                  <RefreshCw size={isExpanded ? 24 : 18} className="spinner" />
                </div>
              )}
            </div>

            {/* Action buttons in header bar */}
            <div className="header-actions-bar">
              {onSettings && (
                <button className="btn-header btn-icon" onClick={onSettings} title="Settings">
                  <Settings size={16} />
                  <span className="desktop-only">Settings</span>
                </button>
              )}
              
              {/* {(guideData && onUpdateGuide) && (
                <button className="btn-header btn-sm" onClick={onUpdateGuide}>
                  <RefreshCw size={16} />
                  <span>Update</span>
                </button>
              )} */}
              
              {/* Collapse/Expand toggle when studying */}
              {isStudying && (
                <button 
                  className="collapse-toggle"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
              )}
            </div>
          </div>

          {/* Collapsible content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div 
                className="header-expandable"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="header-content">
                  {/* Left section - Quota and Plan Info */}
                  <div className="header-left">
                    {quotaStatus && (
                      <div className="quota-info">
                        <div className="quota-stat">
                          <span className="quota-number">{quotaStatus.guides?.remaining || 0}</span>
                          <span className="quota-label">guides left this month</span>
                        </div>
                        <div className="plan-badge">
                          <span className="plan-type">{quotaStatus.plan_type || 'free'} plan</span>
                        </div>
                      </div>
                    )}
                    
                    {guideData && (
                      <div className="guide-stats">
                        <div className="guide-info">
                          <span className="guide-title">{guideData.title || 'Study Guide'}</span>
                          <div className="guide-details">
                            <span className="guide-detail">{guideData.topics?.length || 0} Topics</span>
                            <span className="guide-progress">{progressPercentage}% Complete</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {useTestData && (
                      <div className="test-mode-badge">
                        🧪 TEST MODE ACTIVE
                      </div>
                    )}
                  </div>

                  {/* Right section - Action Buttons */}
                  <div className="header-right">
                    <div className="header-actions">
                      <button className="btn btn-primary" onClick={onUpdateGuide}>
                        <RefreshCw size={16} />
                        <span>Update Guide</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Auto-hide indicator */}
        {isStudying && !isVisible && !isExpanded && (
          <div className="header-peek-indicator">
            <div className="peek-line"></div>
          </div>
        )}
      </motion.header>
    </AnimatePresence>
  );
};

export default CollapsibleHeader;